import {
  WHITEBOARD_HEIGHT,
  WHITEBOARD_SEGMENT_POINTS,
  WHITEBOARD_WIDTH,
  type BulletinNote,
  type WhiteboardStroke,
} from '@veriverse/shared';

/**
 * Pure helpers behind the whiteboard and bulletin board panels (spec §6.15), kept apart from
 * React and the canvas so they can be unit tested.
 */

/** Board pixels → the half-pixel integers strokes are sent in, clamped to the board. */
export function toHalfPixels(x: number, y: number): [number, number] {
  const clamp = (v: number, max: number) => Math.min(max * 2, Math.max(0, Math.round(v * 2)));
  return [clamp(x, WHITEBOARD_WIDTH), clamp(y, WHITEBOARD_HEIGHT)];
}

/**
 * Collects the points of one pen stroke and cuts it into segments that fit one message. Each
 * segment after the first starts at the previous one's last point, so the line has no gaps.
 */
export class StrokeBuilder {
  private current: number[] = [];

  constructor(readonly group: string) {}

  /** Adds a point (half pixels); returns a full segment to send, if one is complete. */
  add(x: number, y: number): number[] | undefined {
    const n = this.current.length;
    if (n >= 2 && this.current[n - 2] === x && this.current[n - 1] === y) return undefined;
    this.current.push(x, y);
    if (this.current.length < WHITEBOARD_SEGMENT_POINTS * 2) return undefined;
    const full = this.current;
    this.current = full.slice(-2);
    return full;
  }

  /** The unsent rest when the pen lifts (`undefined` when only the carried-over point is left). */
  finish(hasSent: boolean): number[] | undefined {
    const rest = this.current;
    this.current = [];
    if (rest.length === 0 || (hasSent && rest.length <= 2)) return undefined;
    return rest;
  }

  /** Points not sent yet, for drawing the line while it is being made. */
  pending(): readonly number[] {
    return this.current;
  }
}

/** A short random id for the segments of one pen stroke. */
export function newStrokeGroup(random: () => number = Math.random): string {
  return Array.from({ length: 10 }, () => Math.floor(random() * 36).toString(36)).join('');
}

/** Distance from a point to a segment, all in board pixels. */
function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t =
    lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * Strokes the eraser touches at a board point: within `radius` px of the line (plus half its
 * width). Only strokes `mayErase` allows (own ones, or all for admins).
 */
export function strokesAt(
  strokes: readonly WhiteboardStroke[],
  x: number,
  y: number,
  radius: number,
  mayErase: (stroke: WhiteboardStroke) => boolean,
): string[] {
  const hits: string[] = [];
  for (const stroke of strokes) {
    if (!mayErase(stroke)) continue;
    const p = stroke.points;
    const reach = radius + stroke.width / 2;
    for (let i = 0; i < p.length; i += 2) {
      const ax = (p[i] ?? 0) / 2;
      const ay = (p[i + 1] ?? 0) / 2;
      const bx = (p[i + 2] ?? p[i] ?? 0) / 2;
      const by = (p[i + 3] ?? p[i + 1] ?? 0) / 2;
      if (distanceToSegment(x, y, ax, ay, bx, by) <= reach) {
        hits.push(stroke.id);
        break;
      }
    }
  }
  return hits;
}

/** Splits ids into chunks of at most `size` (the erase message takes 20 at a time). */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

/** Notes still up at `now`, oldest first. */
export function visibleNotes(notes: readonly BulletinNote[], now: Date): BulletinNote[] {
  const at = now.toISOString();
  return notes.filter((n) => n.expiresAt > at);
}
